import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { getApiUrl, getCsrfToken } from "../../../utils/apiHelper";
import StatusResultModal from "../../StatusResultModal";
import ConfirmModal from "../../ConfirmModal";
import { profileTypeLabels } from "../../../utils/categoryLabels";
import { getReadinessLevel } from "../../../utils/readinessUtils";

const parseOverallReadiness = (raw) => {
  const value = typeof raw === "number" ? raw : parseFloat(raw);
  return Number.isFinite(value) ? value : 0;
};

const getProfileType = (report) =>
  report?.profile_type || report?.profile_details_summary?.profile_type || "";

const PROFILE_TYPE_FILTER_OPTIONS = Object.keys(profileTypeLabels).sort((a, b) => {
  if (a === "prompts_docs_llm") return -1;
  if (b === "prompts_docs_llm") return 1;
  return a.localeCompare(b);
});

const CONSOLIDATION_METHODS = [
  { value: "aggregate", labelKey: "overlapPolicyAggregate" },
  // { value: "keep_only_latest", labelKey: "overlapPolicyLatest" },
  // { value: "keep_only_oldest", labelKey: "overlapPolicyOldest" },
  { value: "keep_only_high_severity", labelKey: "overlapPolicyHighSeverityOnly" },
  { value: "keep_only_failed_tests", labelKey: "overlapPolicyAllFailedTestsOnly" },
];

const formatDate = (timestamp) => new Date(timestamp).toLocaleString();

const calculateOverallScore = (reportData) =>
  Math.round(parseOverallReadiness(reportData?.overall_readiness));

const getProfileName = (report) =>
  report.profile_name || report.profile_details_summary?.profile_name || "";

const getProfileTypeLabel = (report) => {
  const profileType = getProfileType(report);
  return profileTypeLabels[profileType] || profileType || "";
};

const normalizeConsolidatedReport = (result) => ({
  ...result,
  overall_readiness: parseOverallReadiness(result.overall_readiness),
  is_consolidated: true,
});

const sortReports = (a, b, sortBy, sortAsc) => {
  const dir = sortAsc ? 1 : -1;
  if (sortBy === "id") return ((Number(a.id) || 0) - (Number(b.id) || 0)) * dir;
  if (sortBy === "created_at") {
    return (
      ((a.created_at ? new Date(a.created_at).getTime() : 0) -
        (b.created_at ? new Date(b.created_at).getTime() : 0)) * dir
    );
  }
  if (sortBy === "overall_score") {
    return (calculateOverallScore(a) - calculateOverallScore(b)) * dir;
  }
  if (sortBy === "profile_name") {
    return String(getProfileName(a)).localeCompare(String(getProfileName(b))) * dir;
  }
  if (sortBy === "profile_type") {
    return String(getProfileTypeLabel(a)).localeCompare(String(getProfileTypeLabel(b))) * dir;
  }
  return String(a[sortBy] || "").localeCompare(String(b[sortBy] || "")) * dir;
};

export default function IndividualReportHistory({
  refreshNonce = 0,
  onLoadingChange,
  onDetailLoadingChange,
  onReportLoaded,
  onActivate,
  onConsolidated,
}) {
  const { t } = useTranslation("reports_recs", { keyPrefix: "reports_page" });
  const navigate = useNavigate();
  const location = useLocation();
  const handledNavReportRef = useRef(false);
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("id");
  const [sortAsc, setSortAsc] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [profileTypeFilter, setProfileTypeFilter] = useState("");
  const [selecting, setSelecting] = useState(false);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [consolidating, setConsolidating] = useState(false);
  const [consolidateError, setConsolidateError] = useState(null);
  const [isConsolidateModalOpen, setIsConsolidateModalOpen] = useState(false);
  const [isProgressModalOpen, setIsProgressModalOpen] = useState(false);
  const [consolidatedReportId, setConsolidatedReportId] = useState(null);
  const [consolidatedReportName, setConsolidatedReportName] = useState("");
  const [consolidationMethod, setConsolidationMethod] = useState("aggregate");
  const [deletingId, setDeletingId] = useState(null);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
  });
  const [pendingDelete, setPendingDelete] = useState(null);
  const availableConsolidationMethods =
    profileTypeFilter === "rto"
      ? CONSOLIDATION_METHODS.filter((method) => method.value === "aggregate")
      : CONSOLIDATION_METHODS;

  useEffect(() => {
    let cancelled = false;

    const fetchHistoryData = async () => {
      try {
        setLoading(true);
        onLoadingChange?.(true);

        const response = await fetch(
          getApiUrl("/api/internal/readiness-score/list-individual-summary/"),
          {
            method: "GET",
            credentials: "include",
            redirect: "follow",
          }
        );

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        if (!cancelled) {
          setHistoryData(Array.isArray(result) ? result : []);
        }
      } catch (error) {
        console.error("Error fetching history data:", error);
        if (!cancelled) setHistoryData([]);
      } finally {
        if (!cancelled) {
          setLoading(false);
          onLoadingChange?.(false);
        }
      }
    };

    fetchHistoryData();
    return () => {
      cancelled = true;
    };
  }, [refreshNonce, onLoadingChange]);

  useEffect(() => {
    if (!availableConsolidationMethods.some((method) => method.value === consolidationMethod)) {
      setConsolidationMethod("aggregate");
    }
  }, [availableConsolidationMethods, consolidationMethod]);

  const handleRowClick = useCallback(async (report) => {
    try {
      onDetailLoadingChange?.(true);

      const response = await fetch(getApiUrl(`/api/internal/readiness-score/getReportById/${report.id}/`), {
        method: "GET",
        credentials: "include",
        redirect: "follow",
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(t("reportNotFound"));
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || errorData.error || t("failedToLoadReport"));
      }
      const detailedReport = await response.json();
      onReportLoaded?.(detailedReport);
    } catch (e) {
      console.error("Error fetching report by id:", e);
      setStatusModal({
        open: true,
        type: "error",
        message: e.message || t("failedToLoadReport"),
      });
    } finally {
      onDetailLoadingChange?.(false);
    }
  }, [onDetailLoadingChange, onReportLoaded, t]);

  useEffect(() => {
    const shouldOpenLatest = location.state?.openLatest;
    if (!shouldOpenLatest || handledNavReportRef.current || loading || historyData.length === 0) return;

    const latestReport = [...historyData].sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    })[0];

    if (latestReport) {
      handledNavReportRef.current = true;
      onActivate?.();
      handleRowClick(latestReport);
      navigate("/reports", { replace: true, state: {} });
    }
  }, [location.state, historyData, loading, navigate, onActivate, handleRowClick]);

  const searchLower = searchQuery.trim().toLowerCase();
  const filteredHistoryData = historyData.filter((report) => {
    if (profileTypeFilter && getProfileType(report) !== profileTypeFilter) return false;
    if (!searchLower) return true;
    const id = String(report.id ?? "").toLowerCase();
    const name = (report.report_name || "").toLowerCase();
    const profileName = getProfileName(report).toLowerCase();
    return (
      id.includes(searchLower) ||
      name.includes(searchLower) ||
      profileName.includes(searchLower)
    );
  });

  const handleProfileTypeChange = (e) => {
    const nextFilter = e.target.value;
    setProfileTypeFilter(nextFilter);
    setSelectedReportIds([]);
    if (!nextFilter) setSelecting(false);
  };

  const handleCancelSelecting = () => {
    if (consolidating) return;
    setSelecting(false);
    setSelectedReportIds([]);
    setConsolidateError(null);
    setIsConsolidateModalOpen(false);
    setIsProgressModalOpen(false);
    setConsolidatedReportId(null);
  };

  const openConsolidateModal = () => {
    if (selectedReportIds.length === 0 || consolidating) return;
    setConsolidatedReportName("");
    setConsolidateError(null);
    setIsConsolidateModalOpen(true);
  };

  const closeConsolidateModal = () => {
    if (consolidating) return;
    setIsConsolidateModalOpen(false);
    setConsolidateError(null);
  };

  const closeCompleteModal = () => {
    setIsProgressModalOpen(false);
    setConsolidateError(null);
    setConsolidatedReportId(null);
  };

  const handleConsolidate = async () => {
    if (selectedReportIds.length === 0 || consolidating) return;

    try {
      setConsolidating(true);
      setConsolidateError(null);
      setConsolidatedReportId(null);
      setIsConsolidateModalOpen(false);
      setIsProgressModalOpen(false);

      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl("/api/internal/readiness-score/consolidate-report/"),
        {
          method: "POST",
          credentials: "include",
          redirect: "follow",
          headers: {
            "Content-Type": "application/json",
            "X-CSRFToken": csrfToken,
          },
          body: JSON.stringify({
            report_ids: selectedReportIds,
            overlap_policy: consolidationMethod,
            persist: true,
            report_name: consolidatedReportName.trim(),
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (!result || typeof result !== "object" || Array.isArray(result)) {
        throw new Error("API response is not a consolidated report object");
      }

      const reportId = result.consolidated_report_id ?? result.id ?? null;
      if (!reportId) {
        throw new Error("API response is missing a consolidated report id");
      }

      setConsolidatedReportId(reportId);
      setSelecting(false);
      setSelectedReportIds([]);
      setIsProgressModalOpen(true);
      onConsolidated?.();
    } catch (err) {
      console.error("Error consolidating reports:", err);
      setConsolidateError(err.message);
      setIsProgressModalOpen(true);
    } finally {
      setConsolidating(false);
    }
  };

  const handleShowConsolidatedReport = async () => {
    if (!consolidatedReportId || consolidating) return;

    try {
      setIsProgressModalOpen(false);
      onDetailLoadingChange?.(true);

      const response = await fetch(
        getApiUrl(`/api/internal/readiness-score/getConsolidatedReportById/${consolidatedReportId}/`),
        {
          method: "GET",
          credentials: "include",
          redirect: "follow",
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const detailedReport = await response.json();
      onReportLoaded?.(normalizeConsolidatedReport(detailedReport));
      setConsolidatedReportId(null);
    } catch (e) {
      console.error("Error fetching consolidated report by id:", e);
      setConsolidateError(e.message);
      setIsProgressModalOpen(true);
    } finally {
      onDetailLoadingChange?.(false);
    }
  };

  const handleReportToggle = (reportId) => {
    setSelectedReportIds((prev) => {
      if (prev.includes(reportId)) {
        return prev.filter((id) => id !== reportId);
      }
      return [...prev, reportId];
    });
  };

  const allFilteredSelected =
    filteredHistoryData.length > 0 &&
    filteredHistoryData.every((report) => selectedReportIds.includes(report.id));

  const handleSelectAll = () => {
    const filteredIds = filteredHistoryData.map((report) => report.id);
    if (allFilteredSelected) {
      setSelectedReportIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedReportIds((prev) => [...new Set([...prev, ...filteredIds])]);
    }
  };

  const handleTableRowClick = (report) => {
    if (selecting) {
      handleReportToggle(report.id);
      return;
    }
    handleRowClick(report);
  };

  const requestDeleteReport = (report, e) => {
    e?.stopPropagation();
    if (deletingId || consolidating) return;
    setPendingDelete(report);
  };

  const handleDeleteReport = async (report) => {
    if (!report || deletingId || consolidating) return;

    try {
      setDeletingId(report.id);
      const csrfToken = await getCsrfToken();
      const response = await fetch(
        getApiUrl(`/api/internal/readiness-score/deleteIndividualReport/${report.id}/`),
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "X-CSRFToken": csrfToken,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      setHistoryData((prev) => prev.filter((r) => r.id !== report.id));
      setSelectedReportIds((prev) => prev.filter((id) => id !== report.id));
      setStatusModal({
        open: true,
        type: "success",
        message: t("deletedReport"),
      });
    } catch (err) {
      console.error("Error deleting individual report:", err);
      setStatusModal({
        open: true,
        type: "error",
        message: err.message || t("failedToDeleteReport"),
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <>
      <ConfirmModal
        open={pendingDelete != null}
        title={t("deleteReport")}
        message={
          pendingDelete
            ? `${t("delete")} "${pendingDelete.report_name || `${t("report")} ${pendingDelete.id}`}"? ${t("thisActionCannotBeUndone")}`
            : ""
        }
        confirmLabel={t("delete")}
        cancelLabel={t("cancel")}
        zIndex={2100}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          const report = pendingDelete;
          setPendingDelete(null);
          if (report) handleDeleteReport(report);
        }}
      />
      <StatusResultModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        zIndex={2100}
        onClose={() =>
          setStatusModal({ open: false, type: "success", message: "" })
        }
      />
      <div className="rh-search-container">
        <div className="target-select-row" style={{ marginBottom: 0 }}>
          <label htmlFor="profile-type-filter">{t("profileType")}</label>
          <select
            id="profile-type-filter"
            value={profileTypeFilter}
            onChange={handleProfileTypeChange}
            disabled={loading || consolidating}
          >
            <option value="">{t("all")}</option>
            {PROFILE_TYPE_FILTER_OPTIONS.map((type) => (
              <option key={type} value={type}>
                {profileTypeLabels[type] || type}
              </option>
            ))}
          </select>
          <span className="rh-profile-type-hint">
            {t("profileTypeConsolidateHint")}
          </span>
        </div>
        <input
          type="text"
          className="rh-search-input"
          placeholder={t("searchByIdNameOrProfile")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className={`rh-profile-table rh-profile-table--individual${selecting ? " rh-profile-table--selectable" : ""}`}>
        <div className="rh-profile-table-header">
          <div className="rh-profile-table-cell rh-profile-table-cell-check">
            <input
              type="checkbox"
              checked={allFilteredSelected}
              onChange={handleSelectAll}
              disabled={!selecting || filteredHistoryData.length === 0 || loading || consolidating}
              aria-label={t("selectAllReports")}
            />
          </div>
          {[
            ["id", t("id"), "rh-profile-table-cell-report"],
            ["report_name", t("name"), "rh-profile-table-cell-name"],
            ["created_at", t("createdAt"), "rh-profile-table-cell-time"],
            ["overall_score", t("overallScore"), "rh-profile-table-cell-score"],
            ["profile_name", t("profileName"), "rh-profile-table-cell-profile-name"],
            ["profile_type", t("profileType"), "rh-profile-table-cell-profile-type"],
          ].map(([key, label, cls]) => (
            <div
              key={key}
              className={`rh-profile-table-cell ${cls}`}
              style={{ cursor: "pointer" }}
              onClick={() => {
                if (sortBy === key) setSortAsc(!sortAsc);
                else {
                  setSortBy(key);
                  setSortAsc(true);
                }
              }}
            >
              {label}{sortBy === key ? (sortAsc ? " ▲" : " ▼") : ""}
            </div>
          ))}
          <div className="rh-profile-table-cell rh-profile-table-cell-actions">
            {t("actions")}
          </div>
        </div>
        <div className="rh-profile-table-body">
          {loading ? (
            <div className="rh-profile-table-row">
              <div className="rh-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "24px" }}>
                <i className="fas fa-spinner fa-spin" style={{ marginRight: "8px" }}></i>
                {t("loadingReports")}
              </div>
            </div>
          ) : filteredHistoryData.length === 0 ? (
            <div className="rh-profile-table-row">
              <div className="rh-profile-table-cell" style={{ gridColumn: "1 / -1", textAlign: "center", padding: "24px" }}>
                {historyData.length === 0
                  ? t("noReportsFound")
                  : searchQuery.trim()
                    ? t("noReportsMatchSearch")
                    : t("noReportsMatchProfileType")}
              </div>
            </div>
          ) : (
            [...filteredHistoryData]
              .sort((a, b) => sortReports(a, b, sortBy, sortAsc))
              .map((report, index) => {
                const isSelected = selectedReportIds.includes(report.id);
                return (
                <div
                  key={report.id || index}
                  className={`rh-profile-table-row${isSelected ? " selected" : ""}`}
                  onClick={() => !consolidating && !deletingId && handleTableRowClick(report)}
                >
                  <div className="rh-profile-table-cell rh-profile-table-cell-check">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleReportToggle(report.id)}
                      onClick={(e) => e.stopPropagation()}
                      disabled={!selecting || consolidating}
                      aria-label={`${t("selectReport")} ${report.id}`}
                    />
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-report">
                    {report.id || t("na")}
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-name">
                    <span title={report.report_name || t("na")}>{report.report_name || t("na")}</span>
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-time">
                    {formatDate(report.created_at || new Date().toISOString())}
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-score">
                    <div className="readiness-bar-container">
                      <span className="readiness-percentage">
                        {calculateOverallScore(report)}%
                      </span>
                      <div className="readiness-bar">
                        <div
                          className={`readiness-fill readiness-${getReadinessLevel(calculateOverallScore(report))}`}
                          style={{ width: `${calculateOverallScore(report)}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-profile-name">
                    <span title={getProfileName(report) || t("na")}>
                      {getProfileName(report) || t("na")}
                    </span>
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-profile-type">
                    {getProfileTypeLabel(report) || t("na")}
                  </div>
                  <div className="rh-profile-table-cell rh-profile-table-cell-actions">
                    <button
                      type="button"
                      className="rh-delete-btn"
                      title={t("deleteReport")}
                      disabled={deletingId === report.id || consolidating || loading}
                      onClick={(e) => requestDeleteReport(report, e)}
                    >
                      {deletingId === report.id ? (
                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                      ) : (
                        <i className="fas fa-trash" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                </div>
                );
              })
          )}
        </div>
      </div>

      <button
        type="button"
        className={`rh-consolidate-fab${profileTypeFilter && !selecting ? " visible" : ""}`}
        onClick={() => setSelecting(true)}
      >
        {t("consolidateReports")}
      </button>

      <div className={`rh-consolidate-action-bar${selecting ? " visible" : ""}`}>
        <span className="rh-selected-count">{selectedReportIds.length} {t("selected")}</span>
        <button
          type="button"
          className="btn btn-primary"
          disabled={selectedReportIds.length === 0 || consolidating}
          onClick={openConsolidateModal}
        >
          {t("consolidate")}
        </button>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleCancelSelecting}
          disabled={consolidating}
        >
          {t("cancel")}
        </button>
      </div>

      {isConsolidateModalOpen && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 9999 }}
          onClick={closeConsolidateModal}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3>{t("consolidateReports")}</h3>
            {consolidateError && (
              <div className="rh-consolidate-error">
                {consolidateError}
              </div>
            )}
            <div className="rh-consolidate-modal-field">
              <label htmlFor="history-consolidated-report-name">{t("reportName")}</label>
              <input
                id="history-consolidated-report-name"
                type="text"
                value={consolidatedReportName}
                onChange={(e) => setConsolidatedReportName(e.target.value)}
                placeholder={t("enterReportName")}
                disabled={consolidating}
              />
            </div>
            <div className="rh-consolidate-modal-field">
              <label htmlFor="history-consolidation-method">{t("consolidationMethod")}</label>
              <select
                id="history-consolidation-method"
                value={consolidationMethod}
                onChange={(e) => setConsolidationMethod(e.target.value)}
                disabled={consolidating}
              >
                {availableConsolidationMethods.map((method) => (
                  <option key={method.value} value={method.value}>
                    {t(method.labelKey)}
                  </option>
                ))}
              </select>
            </div>
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleConsolidate}
              >
                {t("consolidate")}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeConsolidateModal}
                disabled={consolidating}
              >
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      )}

      {consolidating && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 9999
        }}>
          <div style={{
            textAlign: "center",
            color: "white"
          }}>
            <div style={{
              width: "60px",
              height: "60px",
              border: "6px solid rgba(255, 255, 255, 0.3)",
              borderTop: "6px solid white",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 20px"
            }}></div>
            <div style={{ fontSize: "18px", fontWeight: "500" }}>
              {t("consolidatingReports")}
            </div>
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        </div>
      )}

      {isProgressModalOpen && !consolidating && (
        <div
          className="modal-backdrop"
          style={{ zIndex: 9999 }}
          onClick={closeCompleteModal}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ textAlign: "center" }}>
              {consolidateError ? t("consolidationFailed") : t("consolidationComplete")}
            </h3>
            {consolidateError && (
              <div className="rh-consolidate-error">
                {consolidateError}
              </div>
            )}
            <div
              style={{
                display: "flex",
                gap: "8px",
                justifyContent: "center",
                flexWrap: "wrap",
              }}
            >
              {!consolidateError && (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleShowConsolidatedReport}
                  disabled={!consolidatedReportId}
                >
                  {t("showReport")}
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary"
                onClick={closeCompleteModal}
              >
                {t("close")}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
